import React, { forwardRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import Button from '../Button/Button.js';
import 'react-stately';
import { useFocusRing } from 'react-aria';
import Box from '../Box/Box.js';
import { get } from '../../constants.js';
import sx from '../../sx.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

// Similar to {...A, ...B}
// 1. Remove all overlapping types from First
// 2. Add properties from Second
// https://codesandbox.io/s/native-button-with-nested-elementsclick-bnpjg?file=/src/index.ts
/* This type is intentionally not exported to prevent it from being explicitly referenced in the resulting button types. The alternative would
 * be making this public API and re-exporting from the root */
const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
/**
 * An empty function which does nothing.
 */ // eslint-disable-next-line @typescript-eslint/no-empty-function
function noop() {}
const getIsOnlySingleIcon = ({
  children,
  leadingIcon,
  trailingIcon
}) => {
  if (children) {
    return false;
  }
  if (leadingIcon && !trailingIcon) {
    return true;
  }
  if (!leadingIcon && trailingIcon) {
    return true;
  }
  return false;
};

/**
 * Use as a drop-in replacement for buttons in Atlassian products.
 *
 * Extends button ARIA primitive. Provides styles for various needs, normalized behavior across different platforms and devices, and consistent keyboard focus behavior.
 *
 */
const AtlasButton = /*#__PURE__*/React.memo( /*#__PURE__*/forwardRef(({
  appearance = 'default',
  spacing = 'default',
  shouldFitContainer = false,
  isDisabled,
  isSelected,
  leadingIcon,
  trailingIcon,
  overlay,
  testId,
  children,
  UNSAFE_className,
  href,
  onMouseDown: providedOnMouseDown = noop,
  onMouseUp: providedOnMouseUp = noop,
  ...props
}, ref) => {
  const appearances = ['default', 'danger', 'link', 'primary', 'subtle', 'subtle-link', 'warning'];
  const {
    isFocusVisible,
    focusProps
  } = useFocusRing();
  let Component;
  if (appearances.includes(appearance)) {
    Component = StyledAtlasButton;
  } else {
    throw new Error(`Unrecognized Button appearance: ${appearance}. Please choose from the available options for AtlasButton: "default" | "danger" | "link" | "primary" | "subtle" | "subtle-link" | "warning"`);
  }
  const [isActive, setIsActive] = useState(false);
  // Wrap onMouseDown / onMouseUp to manually trigger active state in Firefox
  const onMouseUp = useCallback(event => {
    providedOnMouseUp(event);
    if (isFirefox) {
      setIsActive(false);
    }
  }, [providedOnMouseUp, setIsActive]);
  const onMouseDown = useCallback(event => {
    providedOnMouseDown(event);
    if (isFirefox) {
      setIsActive(true);
    }
  }, [providedOnMouseDown, setIsActive]);
  const isOnlySingleIcon = getIsOnlySingleIcon({
    children,
    leadingIcon,
    trailingIcon
  });

  // disable input when there's an overlay, e.g., loading state
  const hasOverlay = Boolean(overlay);
  const isInteractive = !isDisabled && !hasOverlay;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Component, _extends({
    disabled: isDisabled,
    shouldFitContainer: shouldFitContainer,
    appearance: appearance,
    overlay: overlay,
    spacing: spacing,
    isSelected: isSelected,
    leadingIcon: leadingIcon,
    trailingIcon: trailingIcon,
    isActive: isActive,
    isFocusVisible: isFocusVisible
    // using undefined so that the property doesn't exist when false
    ,
    "data-has-overlay": hasOverlay ? true : undefined,
    "data-testid": testId
    // Due to how click events are set, we need to set active styles
    //  manually in Firefox and wrap onMouseDown/onMouseUp
    ,
    "data-firefox-is-active": isActive ? true : undefined,
    href: isInteractive ? href : undefined,
    className: UNSAFE_className,
    onMouseUp: onMouseUp,
    onMouseDown: onMouseDown,
    isOnlySingleIcon: isOnlySingleIcon,
    ref: ref
  }, focusProps, props), overlay && /*#__PURE__*/React.createElement(Box, {
    as: "span",
    sx: {
      position: 'absolute',
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
  }, overlay), overlay ? /*#__PURE__*/React.createElement(Box, {
    as: "span",
    sx: {
      opacity: 0
    }
  }, children) : children));
}));

/*
  These Atlassian theme values are expressed as functions so that if we decide to make
  them dependent on props in the future, it wouldn't require a significant
  refactor everywhere they are being used.
*/
const getBorderRadius = () => 3;
const getGridSize = () => 8;
const getFontSize = () => 14;
const borderRadius = getBorderRadius();
const gridSize = getGridSize();
const fontSize = getFontSize();
const heights = {
  default: `${gridSize * 4 / fontSize}em`,
  // 32px
  compact: `${gridSize * 3 / fontSize}em`,
  none: 'auto'
};
const lineHeights = {
  default: heights.default,
  compact: heights.compact,
  none: 'inherit'
};
const padding = {
  // 10px gutter
  default: `0 ${gridSize + gridSize / 4}px`,
  compact: `0 ${gridSize + gridSize / 4}px`,
  none: '0'
};
const verticalAlign = {
  default: 'middle',
  compact: 'middle',
  none: 'baseline'
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const colors = {
  background: {
    default: {
      default: 'colors.background.neutral.default.[default]',
      hover: 'colors.background.neutral.default.hovered',
      active: 'colors.background.neutral.default.pressed',
      disabled: 'colors.background.disabled',
      selected: 'colors.background.selected.default.[default]',
      focusSelected: 'colors.background.selected.default.[default]'
    },
    primary: {
      default: 'colors.background.brand.bold.default',
      hover: 'colors.background.brand.bold.hovered',
      active: 'colors.background.brand.bold.pressed',
      disabled: 'colors.background.disabled',
      selected: 'colors.background.selected.default.[default]',
      focusSelected: 'colors.background.selected.default.[default]'
    },
    warning: {
      default: 'colors.background.warning.bold.default',
      hover: 'colors.background.warning.bold.hovered',
      active: 'colors.background.warning.bold.pressed',
      disabled: 'colors.background.disabled',
      selected: 'colors.background.selected.default.[default]',
      focusSelected: 'colors.background.selected.default.[default]'
    },
    danger: {
      default: 'colors.background.danger.bold.default',
      hover: 'colors.background.danger.bold.hovered',
      active: 'colors.background.danger.bold.pressed',
      disabled: 'colors.background.disabled',
      selected: 'colors.background.danger.bold.pressed',
      focusSelected: 'colors.background.selected.default.[default]'
    },
    link: {
      default: 'colors.background.neutral.subtle.default',
      selected: 'colors.background.selected.default.[default]',
      focusSelected: 'colors.background.selected.default.[default]'
    },
    subtle: {
      default: 'colors.background.neutral.subtle.default',
      hover: 'colors.background.neutral.subtle.hovered',
      active: 'colors.background.neutral.subtle.pressed',
      disabled: 'colors.background.neutral.subtle.default',
      selected: 'colors.background.selected.default.[default]',
      focusSelected: 'colors.background.selected.default.[default]'
    },
    'subtle-link': {
      default: 'colors.background.neutral.subtle.default',
      selected: 'colors.background.selected.default.[default]',
      focusSelected: 'colors.background.selected.default.[default]'
    }
  },
  boxShadowColor: {
    default: {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    },
    primary: {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    },
    warning: {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    },
    danger: {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    },
    link: {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    },
    subtle: {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    },
    'subtle-link': {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    }
  },
  color: {
    default: {
      default: 'colors.text.default',
      active: 'colors.text.default',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    },
    primary: {
      default: 'colors.text.inverse',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    },
    warning: {
      default: 'colors.text.warning.inverse',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    },
    danger: {
      default: 'colors.text.inverse',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    },
    link: {
      default: 'colors.link.default',
      hover: 'colors.link.default',
      active: 'colors.link.pressed',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    },
    subtle: {
      default: 'colors.text.default',
      active: 'colors.text.default',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    },
    'subtle-link': {
      default: 'colors.text.subtle',
      hover: 'colors.text.subtle',
      active: 'colors.text.default',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    }
  }
};
const StyledAtlasButton = styled(Button).withConfig({
  displayName: "AtlasButton__StyledAtlasButton",
  componentId: "sc-y4nf1-0"
})(["align-items:baseline;border-width:0;box-sizing:border-box;display:inline-flex;font-size:inherit;font-style:normal;font-weight:500;max-width:100%;position:relative;text-align:center;text-decoration:none;align-items:center;transition:background-color 0.1s ease-out,box-shadow 0.15s cubic-bezier(0.47,0.03,0.49,1.38);white-space:nowrap;user-select:none;height:", ";line-height:", ";padding:", ";border-radius:", ";vertical-align:", ";justify-content:", ";width:", ";background-color:", ";color:", ";&:hover{cursor:pointer;transition:background-color 0s ease-out,box-shadow 0.15s cubic-bezier(0.47,0.03,0.49,1.38);background-color:", ";color:", ";text-decoration:", ";}&:active{outline:none;transition-duration:0s;background-color:", ";color:", ";}&:disabled{cursor:not-allowed;color:var(--ds-text-disabled,#a5adba) !important;background:var(--ds-background-disabled,rgba(9,30,66,0.04));text-decoration:none;}&:focus,.focus-visible{outline:none;transition-duration:0s,0.2s;background-color:", ";box-shadow:0 0 0 2px ", ";}&:focus:not(:focus-visible):not(.focus-visible){box-shadow:0 0 0 2px ", ";}&[data-focus-visible-added]{transition-duration:0s,0.2s;box-shadow:0 0 0 2px ", ";}&:visited{color:", ";}&:-moz-focus-inner{border:0;margin:0;padding:0;}--ds-link:", ";.light-old-ds-theme-mode a{color:var(--ds-link,#172b4d);}", ""], p => p.spacing ? heights[p.spacing] : '2.28571429em', p => p.spacing ? lineHeights[p.spacing] : '2.28571em', p => {
  return p.spacing && p.isOnlySingleIcon ? '0 4px' : p.spacing ? padding[p.spacing] : '';
}, borderRadius + 'px' , p => p.spacing ? verticalAlign[p.spacing] : 'middle', p => p.shouldFitContainer === true ? 'center' : '', p => p.shouldFitContainer === true ? '100%' : '', p => p.appearance && p.isSelected ? get(colors.background[p.appearance].focusSelected) : p.appearance ? get(colors.background[p.appearance].default) : get('colors.background.neutral.default.[default]'), p => p.appearance && p.isSelected ? get(colors.color[p.appearance].focusSelected) : p.appearance ? get(colors.color[p.appearance].default) : get('colors.text.default'), p => p.appearance && p.overlay && p.isSelected ? get(colors.background[p.appearance].focusSelected) : p.appearance && p.overlay ? get(colors.background[p.appearance].default) : p.appearance && p.isSelected ? get(colors.background[p.appearance].focusSelected) : p.appearance ? get(colors.background[p.appearance].hover) : get('colors.background.neutral.default.hovered'), p => p.appearance ? get(colors.color[p.appearance].hover) : get('colors.text.default'), p => !p.isSelected && (p.appearance === 'link' || p.appearance === 'subtle-link') ? 'underline' : 'inherit', p => p.appearance && p.isSelected ? get(colors.background[p.appearance].selected) :
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
p.appearance ? get(colors.background[p.appearance].active) : get('colors.background.neutral.default.pressed'), p => p.appearance && p.isSelected ? get(colors.color[p.appearance].selected) :
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
p.appearance ? get(colors.color[p.appearance].active) : get('colors.background.neutral.default.pressed'), p => p.appearance && p.isSelected ? get(colors.background[p.appearance].focusSelected) : p.appearance ? get(colors.background[p.appearance].focus) : '', p => p.appearance && p.isSelected ? get(colors.boxShadowColor[p.appearance].focus) : p.appearance ? get(colors.boxShadowColor[p.appearance].focus) : '', p => p.appearance && p.isSelected ? get(colors.boxShadowColor[p.appearance].focus) : p.appearance ? get(colors.boxShadowColor[p.appearance].focus) : '', p => p.appearance ? get(colors.boxShadowColor[p.appearance].focus) : '', p => p.appearance === 'primary' ? get('colors.text.inverse') : p.appearance === 'danger' ? get('colors.text.inverse') : p.appearance === 'warning' ? get('colors.text.warning.inverse') : p.appearance === 'link' ? get('colors.link.default') : p.appearance === 'subtle' ? get('colors.text.default') : p.appearance === 'subtle-link' ? get('colors.text.subtle') : get('colors.text.default'), get('colors.text.default'), sx);
AtlasButton.displayName = 'AtlasButton';
var AtlasButton$1 = AtlasButton;

export { AtlasButton$1 as default, getBorderRadius, getFontSize, getGridSize };
